import React, { useCallback, useEffect, useState } from "react";
import { LANGUAGE_EN } from "~/constants";
import { getAllMissingTranslations, matchAllPolyFill } from "~/utils";
import { ALL_TEXTS } from "~/texts/languages";
import translate from "translate";
import _ from "lodash";
import RasputinText from "rylak-common/src/components/RasputinText/RasputinText";

translate.engine = "google"; // "google", "yandex", "libre", "deepl"

// Locale variants like NL_BE, FR_BE, DE_AT are not ISO 639-1 codes.
// Map them to their base language for the translation API.
const LOCALE_TO_ISO639 = { NL_BE: "nl", FR_BE: "fr", DE_AT: "de" };
const toTranslateLanguage = (lang) => LOCALE_TO_ISO639[lang] || lang;

// Output must stay within the RasputinText allowlist, or it is dropped.
const textHighlightMarkup = {
    b: (content) => `<b>${content}</b>`,
    g: (content) => `<span class="rasputin-muted">${content}</span>`,
    l: null, // special treatment for link
    p: null, // special treatment for plural
};

const highlightKeyRegexp = "(" + Object.keys(textHighlightMarkup).join("|") + ")";

function highlightText(text, options = {}) {
    let re = new RegExp(`${highlightKeyRegexp}{([^}]*)}`, "gi");
    for (const match of matchAllPolyFill(re, text)) {
        let [full, characterKey, content] = match;
        if (characterKey === "p") {
            const pluralAndSingular = content.split("|");
            text = text.replace(full, options?.plural === false ? pluralAndSingular[0] : pluralAndSingular[1]);
        } else if (characterKey === "l") {
            text = text.replace(full, `<a href="${content}" target="_blank">${content}</a>`);
        } else {
            text = text.replace(full, textHighlightMarkup[characterKey?.toLowerCase() || ""]?.(content) ?? content);
        }
    }
    return text;
}

let extraTranslationCounterOuter = 0;

const LANGUAGE_DELIMITER = ".";

export default function useGetLocaleText({ language, shopTextOverrides, supportEmail }) {
    const [lastLanguage, setLastLanguage] = useState(language);
    const [extraTranslationCounter, setExtraTranslationCounter] = useState(extraTranslationCounterOuter);
    const [cachedAutoLanguageTranslations, setCachedAutoLanguageTranslations] = useState({});
    const [allTexts, setAllTexts] = useState(ALL_TEXTS);

    useEffect(() => {
        setLastLanguage(language);
    }, [language]);

    useEffect(() => {
        if (shopTextOverrides) {
            setAllTexts({ ...allTexts, ...shopTextOverrides });
        }
    }, [shopTextOverrides]);

    const getLocaleText = useCallback(
        (keys, variables, options = {}, text) => {
            let languageToUse = options?.languageOverride || language || LANGUAGE_EN;

            if (languageToUse !== LANGUAGE_EN && !cachedAutoLanguageTranslations[languageToUse]) {
                // We have not done the auto-translate yet. Use the old language for a bit
                languageToUse = lastLanguage;
            }

            let textObject;
            if (!text) {
                const oneLineKey = Array.isArray(keys) ? keys.join(LANGUAGE_DELIMITER) : keys;
                textObject = shopTextOverrides && shopTextOverrides[oneLineKey];
                if (!textObject) {
                    // We did not have any override. Try the new one-line-key
                    textObject = allTexts[oneLineKey];
                }
                if (!textObject) {
                    // Maybe we still have it the old way
                    textObject = Array.isArray(keys) ? keys.reduce((v, k) => v && v[k], allTexts) : allTexts[keys];
                }
                if (!textObject) {
                    console.log("Translation issue: Could not find text object", keys);
                }
                text = textObject && textObject[languageToUse];
            }
            if (!text && textObject && textObject[LANGUAGE_EN]) {
                // Seems like we have the translation in english but not in the given language.
                // Lets try to translate it from english!
                const autoTranslateKey = Array.isArray(keys) ? keys.join(LANGUAGE_DELIMITER) : keys;

                const autoTranslateKeyIncludingVariablesAndOptions = []
                    .concat(keys)
                    .concat(_.keys(variables) || [])
                    .concat(_.keys(options) || [])
                    .join(LANGUAGE_DELIMITER);

                if (cachedAutoLanguageTranslations?.[languageToUse]?.[autoTranslateKey]) {
                    if (options?.plural) {
                        text =
                            cachedAutoLanguageTranslations?.[languageToUse]?.[autoTranslateKey + LANGUAGE_DELIMITER + "plural"] || cachedAutoLanguageTranslations?.[languageToUse]?.[autoTranslateKey];
                    }
                    text = cachedAutoLanguageTranslations?.[languageToUse]?.[autoTranslateKey];
                }

                const cachedValueIncludingVariablesAndOptions = cachedAutoLanguageTranslations?.[languageToUse]?.[autoTranslateKeyIncludingVariablesAndOptions];

                if (cachedValueIncludingVariablesAndOptions && cachedValueIncludingVariablesAndOptions !== "LOADING") {
                    text = cachedValueIncludingVariablesAndOptions;
                } else if (cachedValueIncludingVariablesAndOptions !== "LOADING") {
                    if (cachedAutoLanguageTranslations[languageToUse]) {
                        // Initial cached translations are loaded. So we are ready to add more
                        cachedAutoLanguageTranslations[languageToUse][autoTranslateKeyIncludingVariablesAndOptions] = "LOADING";
                        // Lets store the translation including each specific variable and option
                        const translatedToEnglishWithVariables = getLocaleText(keys, variables, {
                            ...options,
                            languageOverride: LANGUAGE_EN,
                            raw: true,
                        });
                        let counter = extraTranslationCounterOuter + 1;
                        extraTranslationCounterOuter = counter;
                        translate(translatedToEnglishWithVariables, toTranslateLanguage(languageToUse)).then((translatedText) => {
                            cachedAutoLanguageTranslations[languageToUse][autoTranslateKeyIncludingVariablesAndOptions] = translatedText;
                            setExtraTranslationCounter(counter);
                        });
                    }
                }
            }
            if (options?.capitalize || options?.upperCase) {
                text = text?.toLocaleUpperCase();
            }
            if (options?.lowerCase) {
                text = text?.toLocaleLowerCase();
            }
            if (options?.preFormatFn) {
                text = options.preFormatFn(text);
            }
            const combinedVariables = {
                supportEmail: supportEmail,
                ...variables,
            };

            text &&
                combinedVariables &&
                Object.entries(combinedVariables).forEach(([key, value]) => {
                    if (typeof value === "function") {
                        const regExp = new RegExp(`\\$\{${key}, (.*)}`, "g");
                        const match = [...matchAllPolyFill(regExp, text)];
                        if (match && match.length) {
                            match.forEach(([, matchValue]) => {
                                text = text.replace(regExp, value(matchValue));
                            });
                        }
                    } else {
                        text = text.split("${" + key + "}").join(value);
                    }
                });

            if (!text && languageToUse !== LANGUAGE_EN) {
                // Found no translation, we try to default to english
                return getLocaleText(keys, variables, {
                    ...options,
                    ...{ languageOverride: LANGUAGE_EN },
                });
            }

            return options?.raw
                ? text
                : text && (
                      <span style={{ whiteSpace: "pre-line", ...options?.styles }}>
                          <RasputinText text={highlightText(text, options)} dropUnknownTags />
                      </span>
                  );
        },
        [language, cachedAutoLanguageTranslations, lastLanguage, extraTranslationCounter, shopTextOverrides, supportEmail],
    );

    useEffect(() => {
        if (language === LANGUAGE_EN) {
            return;
        }
        if (!cachedAutoLanguageTranslations[language]) {
            const tempTranslationMapping = {};
            const allMissingTranslations = getAllMissingTranslations(allTexts, {
                language: language,
                delimiter: LANGUAGE_DELIMITER,
            });
            let counter = 0;
            let totalAmount = allMissingTranslations.length;

            const autoTranslateLoopEndingFn = function () {
                counter += 1;
                if (counter === totalAmount) {
                    cachedAutoLanguageTranslations[language] = tempTranslationMapping;
                    setCachedAutoLanguageTranslations({
                        ...cachedAutoLanguageTranslations,
                    });
                }
            };

            allMissingTranslations.forEach(({ key, text }) => {
                const containsPlural = text.includes("p{");

                const convertedText = highlightText(text, { plural: false });
                if (containsPlural) {
                    totalAmount += 1;
                    const convertedTextPlural = highlightText(text, {
                        plural: true,
                    });
                    translate(convertedTextPlural, toTranslateLanguage(language))
                        .then((translatedText) => {
                            tempTranslationMapping[key + LANGUAGE_DELIMITER + "plural"] = translatedText;
                            autoTranslateLoopEndingFn();
                        })
                        .catch(() => {
                            autoTranslateLoopEndingFn();
                        });
                }
                translate(convertedText, toTranslateLanguage(language))
                    .then((translatedText) => {
                        tempTranslationMapping[key] = translatedText;
                        autoTranslateLoopEndingFn();
                    })
                    .catch(() => {
                        autoTranslateLoopEndingFn();
                    });
            });
        }
    }, [language]);

    return getLocaleText;
}
